import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../../club/ClubProvider.tsx'
import { useNotices } from '../../data/courseStatus.ts'
import type { AssetRow } from '../../data/fleet.ts'
import { estimateWeek, HOURS_PER_PERSON_DAY, hoursText, MEDIAN_OF_LAST, weekNotices, type PlanActuals } from '../../data/forecast.ts'
import { useAreas, type AreaRow } from '../../data/geo.ts'
import { roleLabel, useRoles, useRpc } from '../../data/people.ts'
import { settingLines, useTemplateVersions, type Plan, type PlanJob, type PlanOverview, type TaskTypeRow } from '../../data/plan.ts'
import { dayOfMonth, longDay, timeShort, weekdayShort, type RosterPerson } from '../../data/planUi.ts'
import { outsideEmployment, type Proposal } from '../../data/rotation.ts'
import { titleText } from '../../data/staff.ts'
import { Pill } from '../../ui/components.tsx'
import { useWriteAccess } from '../../ui/hooks.ts'
import { NoticeLine } from '../../ui/NoticeLine.tsx'
import { LookStrip } from '../../ui/looks/LookStrip.tsx'
import { frameOfAreas } from './JobSettings.tsx'
import { needsPeople, taskPalette } from './shared.ts'

export interface WeekGridProps {
  /** the days shown: the seven days of the week from `sm:`, a three-day window on a phone (M23 item 26, `weekWindow`) */
  days: string[]; today: string; lang: string; by: 'people' | 'machines'
  plan: Plan; roster: RosterPerson[]; assetById: Map<string, AssetRow>; overview: PlanOverview | null; blocked: (assetId: string) => boolean
  /** the head greenkeeper may send people from the grid ("+" in a cell, the "to staff" row) */
  canAssign: boolean
  /** lines the rotation assistant proposes and nobody has accepted yet: dashed, not links */
  proposals: readonly Proposal[]
  /** the person looking at the grid: their row is marked (M23 item 21) */
  me?: string | null
  onDay(day: string): void
  onAdd(day: string, personId: string): void
  onOpenDay(day: string): void
  /** M25-D: the task types by code, for the chips' pictures (a chip of a type the grid does not know shows none) */
  typeByCode?: ReadonlyMap<string, TaskTypeRow>
}

const LINE = 'border-b border-r border-slate-200 dark:border-night-line'
const STICKY = `sticky left-0 ${LINE}`
const CELL = `h-[4.5rem] p-1 align-top ${LINE}`
/** M23 item 48: the small words of a cell ("Absent", "Parti") at 13 px, no opacity: the audit measured 10–11 px at 3.9:1. */
const LABEL = 'px-0.5 text-[13px] font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-300'

/** The week as a real grid (M18): people (or machines) down the side, the days across, the work in the cells. The name column and the corner stay put
 *  while the days scroll sideways. Read from the device's own copy; the shading of away days comes from the server's overview when the planner has it, the
 *  greyed days before a person's first day or after their last one ("Pas encore arrivé", "Parti") from their memberships (`RosterPerson.employedFrom/Until`).
 *  M23 (items 21, 26, 48): on a phone the page is the only vertical scroller and the grid shows three days (the page chooses them); from `sm:` the seven
 *  columns, today scrolled into view on arrival; the "À pourvoir" row only for whoever can send people; the row of the person looking is marked.
 *  M16-F: the header of each day carries, for a planner, the hours the plan needs against the hours the team has ("besoin 42 h / dispo 48 h",
 *  `estimateWeek` in data/forecast.ts: the medians from `api.plan_actuals` when online, the templates' default durations otherwise, the roster and the
 *  days away), with the rule printed under the grid; above it, the course notices in force on the days shown (M16-B1's `NoticeLine`, each once with the
 *  days it covers). The grid reads the mirror; only the medians come from the server. */
export function WeekGrid({ days, today, lang, by, plan, roster, assetById, overview, blocked, canAssign, proposals, me, onDay, onAdd, onOpenDay, typeByCode }: WeekGridProps) {
  const { t } = useTranslation()
  const { org, site } = useClub()
  const roles = useRoles()
  const planner = useWriteAccess('job.plan').permitted
  const versions = useTemplateVersions(); const notices = useNotices(); const areas = useAreas()
  const actuals = useRpc<PlanActuals>('plan_actuals', { p_org: org.org_id, p_site: site.site_id, p_from: days[0]!, p_to: days[days.length - 1]! }, planner)
  const [showOpen, setShowOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const avail = useMemo(() => new Map((overview?.availability ?? []).map((a) => [`${a.person_id}:${a.local_date}`, a.status])), [overview])
  const areaById = useMemo(() => new Map((areas ?? []).map((a) => [a.id, a as AreaRow])), [areas])
  const shownNotices = useMemo(() => weekNotices(notices, days), [notices, days])
  const medians = actuals.data?.medians ?? null
  const estimate = useMemo(() => (planner ? estimateWeek(days, plan.jobs, versions ?? [], medians, roster, overview?.availability ?? []) : null),
    [planner, days, plan, versions, medians, roster, overview])
  const hoursOf = useMemo(() => new Map((estimate ?? []).map((e) => [e.day, e])), [estimate])
  const unknown = (estimate ?? []).reduce((n, e) => n + e.unknown, 0)
  const chip = (job: PlanJob): ChipLooks => ({ lines: settingLines(job.params, typeByCode?.get(job.task_type_code), lang), frame: frameOfAreas(job.areaIds, areaById) })
  const dayLabel = (d: string) => `${weekdayShort(d, lang)} ${dayOfMonth(d)}`
  const nameOf = useMemo(() => new Map(roster.map((p) => [p.id, p.name])), [roster])
  const machines = useMemo(() => [...assetById.values()].filter((a) => a.lifecycle === 'active' && ['machine', 'vehicle'].includes(a.kind))
    .sort((a, b) => a.readable_tag.localeCompare(b.readable_tag, 'fr', { numeric: true })), [assetById])
  const openByDay = useMemo(() => new Map(days.map((d) => [d, plan.day(d).filter((j) => needsPeople(j))])), [days, plan])
  const anyOpen = [...openByDay.values()].some((l) => l.length > 0)
  const wide = days.length > 3
  // M23 item 26: when the week holds today and the columns overflow the box (a tablet, a narrow window), today's column comes into view on arrival
  useEffect(() => {
    const el = box.current
    if (!el || !days.includes(today)) return
    const th = el.querySelector<HTMLElement>(`th[data-day="${today}"]`)
    const corner = el.querySelector<HTMLElement>('th[data-corner]')
    if (!th || !corner || el.scrollWidth <= el.clientWidth) return
    el.scrollLeft = Math.max(0, th.offsetLeft - corner.offsetWidth)
  }, [days, today])
  const proposed = (match: (p: Proposal) => boolean) => proposals.filter(match).map((p) => (
    <ProposedChip key={p.key} proposal={p} job={plan.byId.get(p.job_id)} sub={by === 'people' ? (p.asset_id ? assetById.get(p.asset_id)?.readable_tag : undefined) : nameOf.get(p.person_id)} />
  ))

  return (
    <>
      {/* M16-F places M16-B1's line: the notices in force on the days shown, each once with its days (a competition on Saturday, a green closed all week) */}
      {shownNotices.length > 0 && (
        <section className="flex flex-col gap-1" aria-label={t('plan.week.notices')} data-testid="week-notices">
          {shownNotices.map((w) => (
            <div key={w.notice.id} className="flex flex-col gap-0.5" data-testid="week-notice" data-notice-id={w.notice.id} data-days={w.days.join(',')}>
              <span className="meta">{w.all ? t('plan.week.noticeAll') : w.days.length === 1 ? dayLabel(w.days[0]!) : `${dayLabel(w.days[0]!)} – ${dayLabel(w.days[w.days.length - 1]!)}`}</span>
              <NoticeLine notice={w.notice} areaById={areaById} lang={lang} />
            </div>
          ))}
        </section>
      )}
    <div ref={box} className="isolate overflow-auto overscroll-contain rounded-xl border border-slate-200 bg-white shadow-sm dark:border-night-line dark:bg-night-card sm:max-h-[calc(100dvh-9rem)]" data-testid="plan-week" data-by={by} data-days={days.length}>
      <table className={`w-full table-fixed border-separate border-spacing-0 text-left text-sm ${wide ? 'min-w-[59.5rem]' : ''}`}>
        <colgroup><col className={wide ? 'w-28' : 'w-24'} />{days.map((d) => <col key={d} />)}</colgroup>
        <thead>
          <tr>
            <th scope="col" data-corner className={`${STICKY} top-0 z-30 bg-slate-50 p-2 text-[13px] font-semibold uppercase tracking-wide text-slate-700 dark:bg-night dark:text-slate-300`}>{by === 'people' ? t('plan.person') : t('plan.machine')}</th>
            {days.map((d) => (
              <th key={d} scope="col" className={`${LINE} sticky top-0 z-20 p-1 ${d === today ? 'bg-brand text-white dark:bg-emerald-800' : 'bg-slate-50 dark:bg-night'}`} data-day={d}>
                <button className="flex min-h-11 w-full flex-col items-center justify-center rounded-lg leading-tight underline-offset-2 hover:underline" onClick={() => onDay(d)} aria-current={d === today ? 'date' : undefined} aria-label={longDay(d, lang)} data-testid="plan-week-day" data-day={d}>
                  <span className="text-[13px] font-semibold uppercase">{weekdayShort(d, lang)}</span>
                  <span className="text-lg font-bold">{dayOfMonth(d)}</span>
                </button>
                {/* M16-F: the hours the plan needs against the hours the team has that day (13 px, the grid's floor); short = a warning, never a refusal */}
                {(() => {
                  const e = hoursOf.get(d)
                  if (!e) return null
                  return (
                    <span className="flex flex-col items-center pb-1 text-[13px] font-medium normal-case leading-tight" data-testid="plan-week-hours" data-day={d}
                      data-need-min={e.neededMin} data-have-min={e.availableMin} data-unknown={e.unknown} data-short={e.short || undefined} title={e.short ? t('plan.hours.short') : undefined}>
                      <span>{t('plan.hours.need', { h: hoursText(e.neededMin, lang) })}</span>
                      <span className={e.short ? (d === today ? 'font-bold underline' : 'font-bold text-sand-ink dark:text-sand') : ''}>{t('plan.hours.have', { h: hoursText(e.availableMin, lang) })}</span>
                      {/* M16a review: the mark is said in a word (a phone has no hover for the title) */}
                      {e.short && <Pill tone="amber"><span data-testid="plan-week-short">{t('plan.hours.shortWord')}</span></Pill>}
                    </span>
                  )
                })()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {canAssign && (
            <tr data-testid="plan-week-open">
              <th scope="row" className={`${STICKY} z-10 bg-sand p-2 align-top dark:bg-night`}>
                <span className="block font-semibold text-sand-ink dark:text-sand">{t('plan.week.open')}</span>
                {anyOpen && <button className="mt-1 min-h-11 text-[13px] font-semibold underline underline-offset-2" aria-expanded={showOpen} onClick={() => setShowOpen(!showOpen)} data-testid="plan-open-expand">{showOpen ? t('plan.week.hideJobs') : t('plan.week.showJobs')}</button>}
              </th>
              {days.map((d) => {
                const open = openByDay.get(d) ?? []
                return (
                  <td key={d} className={`${CELL} bg-sand/40 dark:bg-sand/5`} data-day={d}>
                    <div className="flex flex-col gap-1">
                      {open.length === 0 ? <span className="px-1 text-[13px] text-slate-700 dark:text-slate-300">{t('plan.week.openNone')}</span> : (
                        <button className="flex min-h-11 w-full items-center justify-center rounded-lg border-2 border-sand-ink/30 bg-white px-2 text-sm font-semibold text-sand-ink active:bg-sand dark:border-sand/30 dark:bg-night-card dark:text-sand" onClick={() => onOpenDay(d)} data-testid="plan-open-day" data-day={d} data-count={open.length}>
                          {t('plan.week.openCount', { count: open.length })}
                        </button>
                      )}
                      {showOpen && open.map((j) => <Chip key={j.id} job={j} looks={chip(j)} />)}
                    </div>
                  </td>
                )
              })}
            </tr>
          )}
          {by === 'machines' && machines.map((m) => (
            <tr key={m.id} data-testid="plan-week-machine" data-asset={m.id}>
              <th scope="row" className={`${STICKY} z-10 bg-white p-2 align-top dark:bg-night-card`}>
                <span className="block font-semibold">{m.readable_tag}</span>
                {blocked(m.id) && <Pill tone="red">{t('plan.out')}</Pill>}
                <span className="meta block truncate font-normal">{m.model ?? ''}</span>
              </th>
              {days.map((d) => (
                <td key={d} className={CELL} data-testid="plan-week-cell" data-asset={m.id} data-day={d}>
                  <div className="flex flex-col gap-1">
                    {plan.ofAsset(m.id, d).map(({ job, assignment }) => <Chip key={assignment.id} job={job} looks={chip(job)} sub={nameOf.get(assignment.person_id)} />)}
                    {proposed((p) => p.asset_id === m.id && p.local_date === d)}
                  </div>
                </td>
              ))}
            </tr>
          ))}
          {by === 'people' && roster.map((p) => {
            const mine = !!me && p.id === me
            // M23 item 21: the row of the person looking stands out (a tint and a bar on its name: a ring on a table row does not draw)
            const tint = mine ? 'bg-fairway-light/70 dark:bg-emerald-950/40' : ''
            return (
              <tr key={p.id} data-testid="plan-week-person" data-person={p.id} data-me={mine || undefined}>
                <th scope="row" className={`${STICKY} z-10 p-2 align-top ${mine ? 'border-l-4 border-l-brand bg-fairway-light dark:border-l-fairway dark:bg-emerald-950/60' : 'bg-white dark:bg-night-card'}`}>
                  <span className="block font-semibold leading-tight">{p.name}</span>
                  {/* M20: the person's fonction (club data); the access roles only for someone who has none (an access login, a contractor) */}
                  <span className="meta block font-normal" data-testid="plan-week-fonction">{p.fonction ? titleText(p.fonction, lang) : p.roles.map((r) => roleLabel(roles ?? [], r, lang)).join(', ')}</span>
                </th>
                {days.map((d) => {
                  // M20: a day before the person's first day or after their last one is greyed and takes nobody new (what is already planned there stays, to be given away)
                  const gone = outsideEmployment(d, p.employedFrom, p.employedUntil)
                  const st = gone ? undefined : avail.get(`${p.id}:${d}`)
                  return (
                    <td key={d} className={`${CELL} ${gone ? 'bg-slate-200 text-slate-500 dark:bg-night dark:text-slate-400' : st === 'off' ? 'bg-slate-200/80 dark:bg-slate-800' : st === 'partial' ? 'bg-slate-100 dark:bg-slate-900' : tint}`}
                      data-testid="plan-week-cell" data-person={p.id} data-day={d} data-avail={st} data-employment={gone ?? undefined}>
                      <div className="flex h-full flex-col gap-1">
                        {gone && <span className={LABEL} data-testid="plan-week-employment">{t(gone === 'before' ? 'plan.week.notYet' : 'plan.week.gone')}</span>}
                        {st && <span className={LABEL} data-testid="plan-week-avail">{t(`plan.avail.${st}`)}</span>}
                        {plan.ofPerson(p.id, d).map(({ job, assignment }) => {
                          const asset = assignment.asset_id ? assetById.get(assignment.asset_id) : undefined
                          return <Chip key={assignment.id} job={job} looks={chip(job)} sub={asset?.readable_tag} out={!!asset && blocked(asset.id)} />
                        })}
                        {proposed((x) => x.person_id === p.id && x.local_date === d)}
                        {canAssign && !gone && (
                          <button className="mt-auto flex min-h-11 w-full items-center justify-center rounded-lg border border-dashed border-slate-300 text-lg font-semibold text-slate-500 active:bg-fairway-light dark:border-night-line dark:text-slate-400 dark:active:bg-night"
                            aria-label={t('plan.week.addFor', { name: p.name, day: longDay(d, lang) })} onClick={() => onAdd(d, p.id)} data-testid="plan-cell-add">+</button>
                        )}
                      </div>
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
      {/* M16-F: the rule of the estimate, printed (the plan's sentence: "médiane des 12 derniers travaux, sans météo ni état du terrain"); what it rests on when offline */}
      {estimate && (
        <p className="meta" data-testid="week-hours-rule" data-medians={medians ? 'server' : 'none'}>
          {t('plan.hours.rule', { count: MEDIAN_OF_LAST, hours: HOURS_PER_PERSON_DAY })}
          {estimate.some((e) => e.short) && <> {t('plan.hours.shortRule', { word: t('plan.hours.shortWord') })}</>}
          {!medians && <> {t('plan.hours.offline')}</>}
          {unknown > 0 && <> {t('plan.hours.unknown', { count: unknown })}</>}
        </p>
      )}
    </>
  )
}

/** One job in a cell: its title on two lines at most, the machine (or the person, by machine) and the start time; coloured by kind of task. 44 px: the grid's
 *  own exception (seven columns on a phone, M18); 13 px, the audit's floor. */
interface ChipLooks { lines: ReturnType<typeof settingLines>; frame: ReturnType<typeof frameOfAreas> }
function Chip({ job, sub, out, looks }: { job: PlanJob; sub?: string; out?: boolean; looks?: ChipLooks }) {
  const { t } = useTranslation()
  const time = timeShort(job.scheduled_start_local)
  // M25-D: one picture of the direction or pattern and one of the lap after the title (18 px; the cell keeps its 44 px): the words stay on the job's page
  const pictured = looks?.lines.filter((l) => l.look) ?? []
  const hasLooks = pictured.length > 0
  return (
    <Link to="/plan/job/$jobId" params={{ jobId: job.id }} data-testid="plan-chip" data-job={job.id}
      className={`block min-h-11 rounded-md border border-l-4 px-1.5 py-1 text-[13px] leading-snug ${taskPalette(job.task_type_code)}`}>
      <span className="line-clamp-2 font-semibold">{job.title_text}</span>
      {(sub || time || hasLooks) && (
        <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5">
          {sub && <span className="truncate font-medium">{sub}</span>}
          {out && <span className="font-bold text-flag dark:text-red-300">{t('plan.out')}</span>}
          {time && <span>{time}</span>}
          {hasLooks && <LookStrip lines={looks!.lines} frame={looks!.frame} families={['dir', 'pat', 'lap']} className="text-[18px]" testId="plan-chip-looks" />}
          {hasLooks && <span className="sr-only">{pictured.map((l) => `${l.label} ${l.value}`).join(', ')}</span>}
        </span>
      )}
    </Link>
  )
}

/** A line the rotation assistant proposes: dashed, not a link, nothing is saved yet. */
function ProposedChip({ proposal, job, sub }: { proposal: Proposal; job: PlanJob | undefined; sub?: string }) {
  const { t } = useTranslation()
  return (
    <span data-testid="plan-chip-proposed" data-job={proposal.job_id} data-person={proposal.person_id}
      className={`block min-h-11 rounded-md border-2 border-dashed px-1.5 py-1 text-[13px] leading-snug ${taskPalette(proposal.task_type_code)}`}>
      <span className="block text-[13px] font-bold uppercase tracking-wide">{t('plan.week.proposed')}</span>
      <span className="line-clamp-2 font-semibold">{job?.title_text ?? '?'}</span>
      {sub && <span className="block truncate">{sub}</span>}
    </span>
  )
}
